import React, { useState, useEffect, useCallback } from 'react';
import { GET, POST, PUT, PATCH, DELETE } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import {
  CalendarRange, Plus, Pencil, Trash2, X, CheckCircle, AlertTriangle, Info, XCircle,
  RefreshCw, Lock, ChevronLeft, ChevronRight, Search
} from 'lucide-react';

// --- Global Styles ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

// --- Shared Components (same patterns as Setting.js) ---
const CustomToast = ({ message, severity, onClose }) => {
  if (!message) return null;
  const styles = {
    success: { bg: 'bg-emerald-600', icon: <CheckCircle className="w-6 h-6" /> },
    info: { bg: 'bg-blue-600', icon: <Info className="w-6 h-6" /> },
    warn: { bg: 'bg-amber-500', icon: <AlertTriangle className="w-6 h-6" /> },
    error: { bg: 'bg-red-500', icon: <XCircle className="w-6 h-6" /> },
  };
  const style = styles[severity] || styles.info;
  return (
    <div className={`fixed bottom-6 right-6 z-[100] px-6 py-3 rounded-xl shadow-2xl text-white flex items-center gap-3 animate-fade-in-up max-w-md ${style.bg}`}>
      {style.icon}
      <div className="flex-1"><div className="font-bold text-sm uppercase tracking-wide">{severity}</div><div className="text-sm font-medium">{message}</div></div>
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
    </div>
  );
};

const CustomConfirmDialog = ({ isOpen, header, message, confirmLabel = 'Confirm', onAccept, onReject }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[999] p-4 animate-fade-in-up">
      <div className="bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 w-full max-w-sm text-center">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50"><AlertTriangle className="w-6 h-6" /></div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">{header}</h3>
        <p className="text-slate-500 mb-6 font-medium text-sm">{message}</p>
        <div className="flex gap-3">
          <button onClick={onReject} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
          <button onClick={onAccept} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors shadow-lg shadow-red-200">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    upcoming: 'bg-blue-50 text-blue-700 border-blue-100',
    closed: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border uppercase ${map[status] || map.closed}`}>
      {status || '-'}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  // Tenure bounds are stored as UTC day boundaries, so render them in UTC to avoid
  // a timezone shift making 2025-04-01 look like 2025-03-31.
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** "2025-04-01" for an <input type="date"> value, read in UTC. */
const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

// --- Create / Edit Modal ---
const TenureModal = ({ isOpen, onClose, data, cities, chapters, onSave, isSaving, onCityChange }) => {
  const [form, setForm] = useState({ cityId: '', chapterId: '', name: '', startDate: '', endDate: '', notes: '' });
  const [confirmHistorical, setConfirmHistorical] = useState(false);
  const master = useMasterChapter();

  const isEdit = Boolean(data);
  const isClosed = isEdit && data.status === 'closed';

  useEffect(() => {
    if (!isOpen) return;
    if (data) {
      setForm({
        cityId: data.city_id || '',
        chapterId: data.chapter_id || '',
        name: data.name || '',
        startDate: toDateInput(data.startDate),
        endDate: toDateInput(data.endDate),
        notes: data.notes || '',
      });
      if (data.city_id) onCityChange(data.city_id);
    } else {
      // New tenure: pre-fill the master chapter (still editable)
      setForm({ cityId: master.cityId, chapterId: master.chapterId, name: '', startDate: '', endDate: '', notes: '' });
      if (master.cityId) onCityChange(master.cityId);
    }
    setConfirmHistorical(false);
  }, [isOpen, data, onCityChange, master.cityId, master.chapterId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const datesMoved =
    isEdit && (form.startDate !== toDateInput(data.startDate) || form.endDate !== toDateInput(data.endDate));

  const invalidRange = form.startDate && form.endDate && form.startDate > form.endDate;

  const canSave =
    form.name.trim() &&
    form.startDate &&
    form.endDate &&
    !invalidRange &&
    (isEdit || form.chapterId) &&
    !(isClosed && datesMoved && !confirmHistorical);

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      name: form.name.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
      notes: form.notes,
    };
    if (!isEdit) payload.chapterId = form.chapterId;
    if (isClosed && datesMoved) payload.allowHistoricalEdit = true;
    onSave(payload, isEdit ? 'edit' : 'add', isEdit ? data._id : null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in-up">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">{isEdit ? 'Edit Tenure' : 'Add New Tenure'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar">
          {!isEdit ? (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">City</label>
                <select
                  value={form.cityId}
                  onChange={(e) => { setForm((f) => ({ ...f, cityId: e.target.value, chapterId: '' })); onCityChange(e.target.value); }}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select city</option>
                  {cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Chapter</label>
                <select
                  value={form.chapterId}
                  onChange={set('chapterId')}
                  disabled={!form.cityId}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 disabled:opacity-50"
                >
                  <option value="">Select chapter</option>
                  {chapters.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1.5 ml-1">A tenure belongs to one chapter and cannot be moved later.</p>
              </div>
            </>
          ) : (
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-sm font-medium text-slate-600">
              {data.city_name || data.city_id} &nbsp;/&nbsp; {data.chapter_name || data.chapter_id}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Tenure Name</label>
            <input
              type="text" value={form.name} onChange={set('name')} placeholder="e.g. 2025-26"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={set('startDate')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">End Date</label>
              <input type="date" value={form.endDate} onChange={set('endDate')}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {invalidRange && (
            <p className="text-sm font-bold text-red-600 flex items-center gap-2"><XCircle className="w-4 h-4" /> Start date must be before end date.</p>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Notes (optional)</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {isClosed && datesMoved && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">This tenure has already ended</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Records belong to a tenure by their own date, so moving these dates will
                    reassign historical references, business and one-to-ones between terms.
                  </p>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                    <input type="checkbox" checked={confirmHistorical} onChange={(e) => setConfirmHistorical(e.target.checked)}
                      className="h-4 w-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500" />
                    <span className="text-sm font-bold text-amber-800">I understand, change the dates anyway</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={isSaving || !canSave}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors shadow-lg shadow-blue-200 disabled:opacity-50 disabled:shadow-none flex justify-center items-center gap-2">
            {isSaving && <RefreshCw className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Page ---
const TenurePage = () => {
  const [tenures, setTenures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [cities, setCities] = useState([]);
  const [filterChapters, setFilterChapters] = useState([]);
  const [modalChapters, setModalChapters] = useState([]);

  const master = useMasterChapter();
  const [filters, setFilters] = useState({ city: master.cityId, chapter: master.chapterId, status: '', search: '' });

  // Master chapter filter (super-admin header) locks the page's city/chapter filter
  useEffect(() => {
    setFilters((f) => ({ ...f, city: master.cityId, chapter: master.chapterId }));
    setPage(1);
  }, [master.cityId, master.chapterId]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total_records: 0, total_pages: 0, limit: 20 });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const [toast, setToast] = useState(null);
  const [toastSeverity, setToastSeverity] = useState('info');
  const [confirm, setConfirm] = useState({ isOpen: false });

  const notify = (message, severity = 'info') => { setToastSeverity(severity); setToast(message); };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // --- Dropdown data ---
  useEffect(() => {
    (async () => {
      try {
        const res = await GET('/tenures/cities');
        setCities((res && res.data) || []);
      } catch (e) {
        notify(e.message || 'Failed to load cities', 'error');
      }
    })();
  }, []);

  const loadChaptersFor = useCallback(async (cityId, target) => {
    if (!cityId) { target([]); return; }
    try {
      const res = await GET(`/tenures/chapters/city/${cityId}`);
      target((res && res.data) || []);
    } catch (e) {
      notify(e.message || 'Failed to load chapters', 'error');
      target([]);
    }
  }, []);

  useEffect(() => { loadChaptersFor(filters.city, setFilterChapters); }, [filters.city, loadChaptersFor]);

  const handleModalCityChange = useCallback((cityId) => {
    loadChaptersFor(cityId, setModalChapters);
  }, [loadChaptersFor]);

  // --- List ---
  const fetchTenures = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (filters.city) params.append('city', filters.city);
      if (filters.chapter) params.append('chapter', filters.chapter);
      if (filters.status) params.append('status', filters.status);
      if (filters.search.trim()) params.append('search', filters.search.trim());

      const res = await GET(`/tenures?${params.toString()}`);
      setTenures((res && res.data) || []);
      if (res && res.pagination) setPagination(res.pagination);
    } catch (e) {
      notify(e.message || 'Failed to load tenures', 'error');
      setTenures([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchTenures(); }, [fetchTenures]);

  // --- Mutations ---
  const handleSave = async (payload, mode, id) => {
    setIsSaving(true);
    try {
      const res = mode === 'edit' ? await PUT(`/tenures/${id}`, payload) : await POST('/tenures', payload);
      notify((res && res.message) || 'Saved', 'success');
      setShowModal(false);
      setEditing(null);
      fetchTenures();
    } catch (e) {
      // Overlap / validation errors arrive here as 400s with a useful message.
      notify(e.message || 'Failed to save tenure', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = (row) => {
    setConfirm({
      isOpen: true,
      header: 'Close this tenure?',
      confirmLabel: 'Close tenure',
      message: `"${row.name}" will end now. Activity recorded after this moment falls outside the tenure.`,
      onAccept: async () => {
        setConfirm({ isOpen: false });
        try {
          const res = await PATCH(`/tenures/${row._id}/close`);
          notify((res && res.message) || 'Tenure closed', 'success');
          fetchTenures();
        } catch (e) {
          notify(e.message || 'Failed to close tenure', 'error');
        }
      },
    });
  };

  const handleDelete = (row) => {
    setConfirm({
      isOpen: true,
      header: 'Delete this tenure?',
      confirmLabel: 'Delete',
      message: `"${row.name}" will no longer be selectable in reports. No references, business or meeting records are deleted.`,
      onAccept: async () => {
        setConfirm({ isOpen: false });
        try {
          const res = await DELETE(`/tenures/${row._id}`);
          notify((res && res.message) || 'Tenure deleted', 'success');
          fetchTenures();
        } catch (e) {
          notify(e.message || 'Failed to delete tenure', 'error');
        }
      },
    });
  };

  const setFilter = (key) => (e) => {
    const value = e.target.value;
    setPage(1);
    setFilters((f) => (key === 'city' ? { ...f, city: value, chapter: '' } : { ...f, [key]: value }));
  };

  return (
    <div>
      <CustomStyles />
      <CustomToast message={toast} severity={toastSeverity} onClose={() => setToast(null)} />
      <CustomConfirmDialog
        isOpen={confirm.isOpen}
        header={confirm.header}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        onAccept={confirm.onAccept}
        onReject={() => setConfirm({ isOpen: false })}
      />
      <TenureModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        data={editing}
        cities={cities}
        chapters={modalChapters}
        onSave={handleSave}
        isSaving={isSaving}
        onCityChange={handleModalCityChange}
      />

      <div>
        {/* Header */}
        <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <CalendarRange className="w-7 h-7 text-blue-600" /> Tenure
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Chapter terms. Reports and dashboards can be scoped to a tenure period.
            </p>
          </div>
          <button
            onClick={() => { setEditing(null); setModalChapters([]); setShowModal(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-blue-200 transition-all"
          >
            <Plus className="w-5 h-5" /> Add Tenure
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">City</label>
              <select value={filters.city} onChange={setFilter('city')} disabled={!!master.chapterId}
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 disabled:opacity-50">
                <option value="">All cities</option>
                {cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Chapter</label>
              <select value={filters.chapter} onChange={setFilter('chapter')} disabled={!filters.city || !!master.chapterId}
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 disabled:opacity-50">
                <option value="">All chapters</option>
                {filterChapters.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Status</label>
              <select value={filters.status} onChange={setFilter('status')}
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="upcoming">Upcoming</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" value={filters.search} onChange={setFilter('search')} placeholder="Tenure name"
                  className="w-full pl-9 p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" />
              </div>
            </div>
          </div>
          {master.chapterId && <div className="mt-3"><MasterChapterLockNote /></div>}
        </div>

        {/* Table */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  {['Tenure', 'City', 'Chapter', 'Period', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-400 font-medium">Loading...</td></tr>
                ) : tenures.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-12 text-slate-400 font-medium">
                    No tenures found. Add one to start scoping reports by term.
                  </td></tr>
                ) : tenures.map((row) => (
                  <tr key={row._id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">
                      <div className="text-sm font-bold text-slate-800">{row.name}</div>
                      {row.notes ? <div className="text-xs text-slate-400 max-w-xs truncate">{row.notes}</div> : null}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{row.city_name || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{row.chapter_name || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-700">
                      {formatDate(row.startDate)} &ndash; {formatDate(row.endDate)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600"><StatusBadge status={row.status} /></td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button title="Edit"
                          onClick={() => { setEditing(row); setShowModal(true); }}
                          className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        {row.status === 'active' && (
                          <button title="Close tenure now"
                            onClick={() => handleClose(row)}
                            className="p-2 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors">
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                        <button title="Delete"
                          onClick={() => handleDelete(row)}
                          className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex flex-wrap gap-3 justify-between items-center px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <span className="text-sm font-medium text-slate-500">
                {pagination.total_records} tenure{pagination.total_records === 1 ? '' : 's'} &middot; page {page} of {pagination.total_pages}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                  className="p-2 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                </button>
                <button onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))} disabled={page >= pagination.total_pages}
                  className="p-2 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenurePage;
